import React, { useEffect, useState } from 'react';
import API_BASE_URL from '../../config/api';

const ROLE_OPTIONS = ['guest', 'client', 'admin'];

export default function AdminAccessView() {
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [approvalDrafts, setApprovalDrafts] = useState({});

  const getToken = () => localStorage.getItem('authToken');

  const fetchAccessData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const token = getToken();
      if (!token) {
        throw new Error('로그인이 필요합니다.');
      }

      const [requestsResponse, usersResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/access-requests?status=pending`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch(`${API_BASE_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        }),
      ]);

      const requestsData = await requestsResponse.json();
      const usersData = await usersResponse.json();

      if (!(requestsResponse.ok && requestsData.status === 'success')) {
        throw new Error(requestsData.message || '승인 대기 요청을 불러오지 못했습니다.');
      }

      if (!(usersResponse.ok && usersData.status === 'success')) {
        throw new Error(usersData.message || '사용자 목록을 불러오지 못했습니다.');
      }

      setRequests(requestsData.requests || []);
      setUsers(usersData.users || []);
      setApprovalDrafts((current) => {
        const nextDrafts = { ...current };
        (requestsData.requests || []).forEach((requestItem) => {
          if (!nextDrafts[requestItem.id]) {
            const suggestedUsername = requestItem.email.split('@')[0].toLowerCase().replace(/[^a-z0-9._-]+/g, '');
            nextDrafts[requestItem.id] = {
              username: suggestedUsername,
              password: '',
              role: requestItem.requestedRole || 'guest',
            };
          }
        });
        return nextDrafts;
      });
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '관리자 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessData();
  }, []);

  const updateApprovalDraft = (requestId, key, value) => {
    setApprovalDrafts((current) => ({
      ...current,
      [requestId]: {
        ...(current[requestId] || {}),
        [key]: value,
      },
    }));
  };

  const handleApprove = async (requestId) => {
    const token = getToken();
    const draft = approvalDrafts[requestId] || {};

    try {
      setErrorMessage('');
      setInfoMessage('');

      const response = await fetch(`${API_BASE_URL}/api/admin/access-requests/${requestId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(draft),
      });

      const data = await response.json();
      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || '요청 승인에 실패했습니다.');
      }

      setInfoMessage(`${data.user?.username || '새 사용자'} 계정이 생성되었습니다.`);
      await fetchAccessData();
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '요청 승인에 실패했습니다.');
    }
  };

  const handleReject = async (requestId) => {
    const token = getToken();

    try {
      setErrorMessage('');
      setInfoMessage('');

      const response = await fetch(`${API_BASE_URL}/api/admin/access-requests/${requestId}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
      });

      const data = await response.json();
      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || '요청 거절에 실패했습니다.');
      }

      setInfoMessage('접근 요청을 거절했습니다.');
      await fetchAccessData();
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '요청 거절에 실패했습니다.');
    }
  };

  const handleRoleChange = async (userId, role) => {
    const token = getToken();

    try {
      setErrorMessage('');
      setInfoMessage('');

      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ role }),
      });

      const data = await response.json();
      if (!(response.ok && data.status === 'success')) {
        throw new Error(data.message || '권한 변경에 실패했습니다.');
      }

      setUsers((current) => current.map((user) => (
        user.id === userId ? data.user : user
      )));
      setInfoMessage(`${data.user.username} 권한이 ${data.user.role}로 변경되었습니다.`);
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message || '권한 변경에 실패했습니다.');
    }
  };

  return (
    <div className="dashboard-admin-wrap">
      <header className="dashboard-header">
        <div>
          <p className="dashboard-mini-label">ADMIN</p>
          <h1>User Access Control</h1>
          <p className="dashboard-header-text">
            Review guest registration requests, create approved accounts, and assign user roles.
          </p>
        </div>
      </header>

      {errorMessage && <div className="dashboard-banner dashboard-banner-error">{errorMessage}</div>}
      {infoMessage && <div className="dashboard-banner dashboard-banner-info">{infoMessage}</div>}

      <section className="dashboard-panel dashboard-panel-wide">
        <div className="dashboard-panel-header">
          <h3>Pending Access Requests</h3>
        </div>

        {loading ? (
          <div className="dashboard-smp-empty">승인 요청을 불러오는 중입니다.</div>
        ) : requests.length === 0 ? (
          <div className="dashboard-smp-empty">현재 대기 중인 신규 게스트 요청이 없습니다.</div>
        ) : (
          <div className="dashboard-admin-grid">
            {requests.map((requestItem) => {
              const draft = approvalDrafts[requestItem.id] || {};
              return (
                <div key={requestItem.id} className="dashboard-admin-card">
                  <div className="dashboard-admin-card-top">
                    <div>
                      <h4>{requestItem.fullName}</h4>
                      <p>{requestItem.email}</p>
                    </div>
                    <span className="dashboard-admin-badge">{requestItem.requestedRole}</span>
                  </div>

                  <p className="dashboard-admin-meta">
                    {requestItem.company || 'No company provided'}
                  </p>
                  {requestItem.message && (
                    <p className="dashboard-admin-note">{requestItem.message}</p>
                  )}

                  <div className="dashboard-admin-form">
                    <input
                      type="text"
                      value={draft.username || ''}
                      placeholder="Username"
                      onChange={(event) => updateApprovalDraft(requestItem.id, 'username', event.target.value)}
                    />
                    <input
                      type="text"
                      value={draft.password || ''}
                      placeholder="Temporary password"
                      onChange={(event) => updateApprovalDraft(requestItem.id, 'password', event.target.value)}
                    />
                    <select
                      value={draft.role || 'guest'}
                      onChange={(event) => updateApprovalDraft(requestItem.id, 'role', event.target.value)}
                    >
                      {ROLE_OPTIONS.map((roleOption) => (
                        <option key={roleOption} value={roleOption}>
                          {roleOption}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="dashboard-admin-actions">
                    <button
                      type="button"
                      className="dashboard-primary-btn"
                      onClick={() => handleApprove(requestItem.id)}
                    >
                      Approve & Create
                    </button>
                    <button
                      type="button"
                      className="dashboard-secondary-btn"
                      onClick={() => handleReject(requestItem.id)}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="dashboard-panel dashboard-panel-wide">
        <div className="dashboard-panel-header">
          <h3>Current Users</h3>
        </div>

        <div className="dashboard-user-table">
          <div className="dashboard-user-table-head">
            <span>User</span>
            <span>Email</span>
            <span>Company</span>
            <span>Role</span>
          </div>

          {users.map((user) => (
            <div key={user.id} className="dashboard-user-table-row">
              <span>{user.username}</span>
              <span>{user.email}</span>
              <span>{user.company || '-'}</span>
              <span>
                <select
                  value={user.role}
                  onChange={(event) => handleRoleChange(user.id, event.target.value)}
                >
                  {ROLE_OPTIONS.map((roleOption) => (
                    <option key={roleOption} value={roleOption}>
                      {roleOption}
                    </option>
                  ))}
                </select>
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
